import _ from 'lodash';
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import connect from 'components/lib/connect';
import CloseTopicsMenu from 'actions/conversation/topics_menu/close_topics_menu';
import ConversationTopics from './conversation_topics';
import OpenTopicsMenu from 'actions/conversation/topics_menu/open_topics_menu';
import SearchTopic from 'actions/conversation/topics_menu/search_topic';
import SelectTopic from 'actions/conversation/topics_menu/select_topic';
import UpdateConversationTopicIds from 'actions/conversation/update_conversation_topic_ids';
import { useExecuteAction } from 'components/hooks/connect_hooks';

function ConversationTopicsActionConnector({
  className,
  conversation,
  customerId,
  lastOpenedAt,
  getTopic,
  topicsImmutable,
  topicsProvider,
  topicSuggestions,
}) {
  // Memoize topics, only regenerate the topic options when the topics immutable changes
  const topics = useMemo(() => {
    return _.map(topicsProvider.findAll(), topic => ({
      id: topic.id,
      disabled: topic.disabled,
      name: topic.getNameWithAncestry(),
    }));
  }, [topicsImmutable, topicsProvider]);

  const executeAction = useExecuteAction();
  const getTopicSuggestions = useCallback(
    () => (topicSuggestions && topicSuggestions.addTopics ? topicSuggestions.addTopics : []),
    [topicSuggestions]
  );
  const getModelVersion = useCallback(() => (topicSuggestions ? topicSuggestions.modelVersion : null), [
    topicSuggestions,
  ]);

  const handleMenuOpen = useCallback(() => {
    let numSuggestionsShown = _.difference(getTopicSuggestions(), conversation.topicIds).length;
    executeAction(OpenTopicsMenu, {
      conversationId: conversation.id,
      numSuggestionsShown,
      numSuggestions: getTopicSuggestions().length,
      modelVersion: getModelVersion(),
    });
  }, [conversation, executeAction, getTopicSuggestions, getModelVersion]);

  const handleMenuClose = useCallback(() => {
    executeAction(CloseTopicsMenu, { conversationId: conversation.id, modelVersion: getModelVersion() });
  }, [conversation, executeAction, getModelVersion]);

  const handleTopicsChange = useCallback(
    updatedTopicIds => {
      updatedTopicIds = _.filter(updatedTopicIds, id => getTopic(id));
      let prevTopicIds = conversation.topicIds;
      let add = _.difference(updatedTopicIds, prevTopicIds);
      let remove = _.difference(prevTopicIds, updatedTopicIds);
      if (add.length || remove.length) {
        let topicChanges = { add, remove };
        executeAction(UpdateConversationTopicIds, {
          id: conversation.id,
          customerId,
          topicChanges,
          modelVersion: getModelVersion(),
        });
      }
    },
    [conversation, customerId, executeAction, getModelVersion] // ignore get topic - the function changes each render but it's fine
  );

  const handleTopicSearch = useCallback(() => {
    executeAction(SearchTopic, { conversationId: conversation.id, modelVersion: getModelVersion() });
  }, [conversation, executeAction, getModelVersion]);

  const handleTopicSelect = useCallback(
    ({ topicId, topicName, action, selectionType }) => {
      const conversationId = conversation.id;
      let wasSuggested = _.includes(getTopicSuggestions(), topicId);
      executeAction(SelectTopic, {
        topicId,
        topicName,
        conversationId,
        action,
        selectionType,
        wasSuggested,
        modelVersion: getModelVersion(),
      });
    },
    [conversation, executeAction, getTopicSuggestions, getModelVersion]
  );

  return (
    <ConversationTopics
      className={className}
      conversation={conversation}
      lastOpenedAt={lastOpenedAt}
      topics={topics}
      topicSuggestions={topicSuggestions}
      onMenuClose={handleMenuClose}
      onMenuOpen={handleMenuOpen}
      onTopicChange={handleTopicsChange}
      onTopicSearch={handleTopicSearch}
      onTopicSelect={handleTopicSelect}
    />
  );
}

ConversationTopicsActionConnector.propTypes = {
  className: PropTypes.string,
  conversation: PropTypes.object.isRequired,
  customerId: PropTypes.string.isRequired,
  getTopic: PropTypes.func,
  lastOpenedAt: PropTypes.string,
  topicsImmutable: PropTypes.any.isRequired,
  topicsProvider: PropTypes.shape({
    findAll: PropTypes.func.isRequired,
  }).isRequired,
  topicSuggestions: PropTypes.object,
};

const ConversationTopicsContainer = connect(mapStateToProps)(ConversationTopicsActionConnector);
export default ConversationTopicsContainer;

ConversationTopicsContainer.propTypes = {
  conversationId: PropTypes.string.isRequired,
  customerId: PropTypes.string.isRequired,
  topicSuggestions: PropTypes.object,
};

function mapStateToProps({ getProvider, isFeatureEnabled }, { conversationId, customerId }) {
  const conversationsProvider = getProvider('conversations');
  const topicsProvider = getProvider('topics');
  const workflow = getProvider('conversationWorkflow').get();
  const lastOpenedAt = workflow.topicsLastOpenedAt;
  const topicSuggestionsProvider = getProvider('topicSuggestions');
  const agentAssistanceConfig = getProvider('agentAssistanceConfig').get();

  let topicSuggestions;
  if (agentAssistanceConfig.isEnabled('topicSuggestions')) {
    topicSuggestions = topicSuggestionsProvider.findBy({
      customerId,
      conversationId,
    });
  }

  return {
    conversation: conversationsProvider.find(conversationId),
    customerId,
    getTopic: id => topicsProvider.findBy({ id }),
    lastOpenedAt,
    topicsImmutable: topicsProvider.immutableStore.binding.get(),
    topicsProvider,
    topicSuggestions,
  };
}



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/customer/conversation_history/topics/conversation_topics_container.jsx